// Loom's DRAFT answer rows, and the one thing a draft must never be: the value
// it is asking about.
//
// A draft is an offer — the rows Focus pre-fills the list builder with, or the
// sentence it puts next to "Use this text". The user adopting it unchanged is
// the COMMON case, so the bytes become the field's value and then the issue.
// That is fine when the field is still open. It is nonsense when the question
// is ABOUT a value that already exists:
//
//   > "The capability is recorded as 'Add an audit trail for admin area changes
//   >  with a searchable log and CSV export.' Does that read the way you meant
//   >  it, or would you word it differently?"
//
// …offered, as Loom's draft, that same sentence verbatim (live, #1081). "Use
// this text" would have written back exactly what was being questioned: an
// objection whose only remedy is the status quo, and one round trip spent to
// reach it. Nothing anywhere compared the draft against the field's persisted
// green, so nothing could catch it.
//
// The rule is deliberately WHOLE-DRAFT, never per-row. A draft for a list field
// is the list; dropping the rows that already exist would hand the list builder
// a pre-fill that silently DELETES the user's other criteria the moment they
// press Done. So a draft either echoes the value (and is dropped entirely) or it
// does not (and ships untouched).

import { normalizeQuote } from "../i18n.js";

import {
  splitAcceptanceCriteria,
  withoutBareHeadingLine,
} from "./breakdown.js";

/** The rows of a value as every other consumer reads them, normalized for
 *  comparison. A prose value is one row; a list value is its rows. */
const rowsOf = (value: string): string[] =>
  splitAcceptanceCriteria(value)
    .map(normalizeQuote)
    .filter((r) => r !== "");

/**
 * Drop ONE leading bullet from a single-row draft, so `["- Site manager"]` and
 * `["- A\n- B"]` agree about what the rows are — the multi-line path strips
 * markers per line (`splitAcceptanceCriteria`) and the one-line path used to
 * keep the dash, which then reached the list builder as a literal "- ".
 *
 * DELIBERATELY NARROWER than `stripLeadingMarker`: bullets only, never a
 * leading `1.` / `1)` / `(1)`. What that refuses to touch, and why each SHOULD
 * be left alone — a one-line draft is the whole offer, so a wrong strip here
 * DELETES words rather than mis-splitting them:
 *   - `["1. Quartal exportieren"]` — a German ordinal date, not a marker.
 *     Stripping gives "Quartal exportieren", which is not what Loom meant.
 *   - `["1. PDF 2. CSV"]` — a flattened enumeration. Stripping only the FIRST
 *     marker yields "PDF 2. CSV", i.e. content silently gone; kept whole, the
 *     user splits it in the list builder in one edit.
 *   - `["1.1 und 2.0 werden unterstuetzt"]` — a version, no marker at all.
 * A leading `-`/`*`/`+` followed by a space has no such reading in either
 * locale, so it is always the widget's own marker leaking back in.
 * (No regex lookbehind — Safari 14 ships this module via apps/web.)
 */
const stripLeadingBullet = (line: string): string =>
  line.replace(/^[-*+]\s+/u, "").trim();

/**
 * HOW MANY ROWS IS THIS DRAFT — the one answer both ends use.
 *
 * A draft is a wire array, and the array is NOT the row count: `draftSchema`
 * allows 400 chars per entry and forbids no newline, and every surface that
 * lands a draft in a control joins the entries with "\n" first. So
 * `["a\nb\nc"]` is one entry and three rows, and it reaches a prose answer box
 * bit-for-bit as the three-alternative glued value the prose guard exists to
 * refuse (live #1085/#1097). Counting `draft.length` waved it through.
 *
 * A ROW IS A LINE. Deliberately NOT `splitAcceptanceCriteria` — that splitter
 * also breaks a SINGLE line on an inline "1. … 2. …" enumeration, which is
 * right for a criteria value the user typed in chat and wrong for a draft,
 * because ordinary one-sentence prose carries those markers:
 *
 *     "I want to 1. pick several mines at once and 2. export them in one file"
 *     "Ich moechte die Kennzahlen vom 1. Quartal und vom 2. Quartal exportieren"
 *     "damit ich die Etappe 1. Vorbereitung und 2. Umsetzung auswerten kann"
 *
 * Each is ONE phrasing of ONE answer; nothing is glued and nothing is mutually
 * exclusive. Counting them as two dropped a correctly-shaped draft AND told the
 * model a reason that was false about its own input. One line is one row, full
 * stop; the multi-line path is `splitAcceptanceCriteria`'s (newline rows, list
 * markers stripped), so a draft written as `["Ziel:", "damit …"]` counts as the
 * one row it is.
 *
 * THE HEADING IS DROPPED BEFORE THE COUNT, NOT AFTER. Both invariants above are
 * old; composing them is what was wrong. `["Ziel:", "damit ich 1. Zeit spare
 * und 2. weniger Fehler mache"]` is a heading plus ONE prose line, and deciding
 * "is this one line?" on the raw join said no — so it went to
 * `splitAcceptanceCriteria`, which drops the heading itself and then, with a
 * single content line left, splits it on the very inline run this function
 * exists to ignore (`["Zeit spare und", "weniger Fehler mache"]`). The prose
 * guards then dropped a correctly-shaped draft and handed the model
 * `draft_dropped: 2 rows` — a reason false about its own one-line input.
 *
 * Returns the CANONICAL rows, not just a count, because the client needs the
 * same bytes it counted: a one-row draft is offered as `rows[0]`, and a list
 * pre-fill takes the rows straight.
 */
export const draftRowsOf = (draft: ReadonlyArray<string>): string[] => {
  const lines = draft
    .join("\n")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
  const body = withoutBareHeadingLine(lines);
  if (body.length <= 1) {
    const single = stripLeadingBullet(body[0] ?? "");
    return single === "" ? [] : [single];
  }
  return splitAcceptanceCriteria(body.join("\n"));
};

/**
 * Does this draft merely re-offer `value`?
 *
 * MODULE-LOCAL. `draftWithoutEcho` is the whole of this file's surface — it is
 * what both call sites (`propose_card`'s needs_input merge, `ask_user`) use,
 * and `draftWithoutEcho(d, [v]).length === 0` says exactly what this predicate
 * does, so exporting it through the readiness barrel added a second name for
 * one answer with nobody asking for it.
 *
 * True when the draft, read as rows, carries the SAME SET of rows the value
 * does — so it is a no-op the user cannot act on. Order is ignored: the same
 * criteria in a different sequence is still the same answer, and the reason to
 * ask was never the ordering.
 *
 * False for an empty draft, an empty value, and for any draft that adds or
 * drops a row — an offer that genuinely changes something is a real offer, and
 * refusing it would cost the user Loom's suggestion.
 */
const draftEchoesValue = (
  draft: ReadonlyArray<string>,
  value: string | undefined,
): boolean => {
  if (value === undefined) return false;
  const valueRows = rowsOf(value);
  if (valueRows.length === 0) return false;
  const draftRows = draft
    .flatMap((row) => rowsOf(row))
    .filter((r) => r !== "");
  if (draftRows.length === 0) return false;
  const want = new Set(valueRows);
  const got = new Set(draftRows);
  if (want.size !== got.size) return false;
  for (const row of got) if (!want.has(row)) return false;
  return true;
};

/**
 * The draft to actually emit: the rows as given, or NOTHING when they echo one
 * of the values the user has already settled.
 *
 * `settled` is every value the question could be about — for a field-bound ask
 * that is the field's own green, and for an unbound one (`field: null`, the
 * shape the live defect took) it is every verified value on the card, because
 * the binding says nothing about which value the prose is quoting.
 */
export const draftWithoutEcho = (
  draft: ReadonlyArray<string>,
  settled: ReadonlyArray<string | undefined>,
): string[] =>
  settled.some((value) => draftEchoesValue(draft, value)) ? [] : [...draft];
